<template>
  <div>
    <QuoteNavTabs />
    <div class="pt-3 px-3">
      <b-row style="margin-bottom: 2.5em;">
        <b-col>
          <span v-if="isSupervisor" class="font-lato fs-20">Historial de Cotizaciones</span>
          <span v-else class="font-lato fs-20">Mis Cotizaciones</span>
        </b-col>
      </b-row>

      <b-row v-if="scope == 'for_accesos'" align-h="between" >
        <b-col cols="12" lg="6" class="mb-2 mb-lg-0">
          <div class="d-flex align-items-center flex-wrap">
            <b-input-group class="search-group mr-2">
              <b-input
                v-model="searchTerm"
                autofocus
                placeholder="Buscar por código, cliente, título, # SAP"
                @keyup.enter="search"
              />
              <b-input-group-append>
                <b-button variant="outline-secondary" @click="search">
                  <font-awesome-icon :icon="['fas', 'search']" fixed-width />
                </b-button>
              </b-input-group-append>
            </b-input-group>

            <b-button
              v-b-tooltip.hover
              title="Limpiar búsqueda"
              variant="outline-secondary"
              class="mt-1 mt-sm-0"
              @click="resetSearch"
            >
              <font-awesome-icon :icon="['far', 'undo-alt']" fixed-width />
            </b-button>

            <b-button
              v-if="queryMode && searchTerm"
              variant="outline-primary"
              class="ml-2 mt-1 mt-lg-0"
              @click="searchSapQuotes = true"
            >
              Buscar en SAP
            </b-button>
          </div>
        </b-col>

        <b-col v-if="isSupervisor" cols="12" lg="6" class="mb-2 mb-lg-0 d-none d-lg-block">
          <div class="text-right">
            <b-dropdown
              id="filter-dropdown"
              ref="filterDropdown"
              dropleft
              variant="primary"
              no-caret
            >
              <template #button-content>
                <div class="d-flex align-items-center">
                  <font-awesome-icon :icon="['far', 'filter']" fixed-width />
                  <span class="ml-2 mr-2">Filtros</span>
                  <div id="filter-count">
                    <span>{{ activeFiltersCount }}</span>
                  </div>
                </div>
              </template>

              <b-dropdown-form style="width: 35rem;" @submit.stop.prevent>
                <b-form-group class="mb-0">
                  <template #label><span>Asesor</span></template>
                  <UserSearchInput
                    :user-id="userFilter"
                    @set-user="onAdvisorSelected"
                    @reset-user="onAdvisorReset"
                  />
                </b-form-group>
              </b-dropdown-form>
            </b-dropdown>
          </div>
        </b-col>
      </b-row>

      <b-row v-show="filters.length > 0" class="mt-2 mb-2">
        <b-col>
          <div class="d-flex align-items-center justify-content-end flex-wrap" style="gap: 0.5rem;">
            <div
              v-for="filter in filters"
              :key="filter.key"
              class="filter-item d-flex align-items-center fs-13"
            >
              <span>{{ filter.text }}</span>
              <button
                type="button"
                class="btn btn-sm p-0 ml-1"
                title="Quitar filtro"
                @click="removeFilter(filter.key)"
              >
                <font-awesome-icon :icon="['far', 'times']" fixed-width />
              </button>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Table caption -->
      <b-row align-h="between" class="mt-2">
        <b-col class="d-none d-lg-block">
          <div v-if="count" class="fs-12">
            <span v-if="queryMode || userFilter" class="text-muted">{{ count.toLocaleString() }} resultados</span>
            <span v-else class="text-info">{{ count.toLocaleString() }} cotizaciones en total</span>
          </div>
        </b-col>
        <b-col class="text-right">
          <b-checkbox v-model="hideIgnored" size="sm" inline>
            Ocultar ignoradas
          </b-checkbox>
          <b-checkbox v-model="hideClosed" size="sm" inline class="mr-2">
            Ocultar cerradas
          </b-checkbox>
        </b-col>
      </b-row>
    </div>

    <!-- Main Table -->
    <b-row align-v="center" class="mb-1 mx-0 mt-1">
      <b-table
        :fields="tableFields"
        :items="quotes"
        head-variant="dark"
        fixed
        hover
        responsive
        show-empty
        no-local-sorting
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :tbody-tr-class="rowClass"
        class="fs-12"
        @sort-changed="sortColumn"
      >

        <!-- Loading spinner / empty search text -->
        <template #empty>
          <div v-if="loading" class="text-center text-info">
            <b-spinner small type="grow"></b-spinner>
            <span class="pl-2">Cargando datos...</span>
          </div>
          <div v-else class="text-center text-danger">
            <span>No hay resultados</span>
          </div>
        </template>

        <!-- Table data -->
        <template v-slot:cell(user_id)="data">
          <span
            v-if="isSupervisor"
            :class="{'text-danger': data.item.user.status == 'inactivo'}"
            @click="onRowClick(data.item)"
          >
            {{ data.item.user | concatName2 | titleize }}
          </span>
        </template>

        <template v-slot:cell(quote_id)="data">
          <b-link :to="`/app/prod/cotizador/${data.item.id}`">
            <span>{{ data.value }}</span>
          </b-link>
        </template>

        <template v-slot:cell(client_name)="data">
          <div @click="onRowClick(data.item)">
            <span v-if="data.value">{{ data.value }}</span>
            <span v-else class="text-warning">
              <font-awesome-icon :icon="['far', 'exclamation-circle']" size="sm" fixed-width />
              <span>Falta Cliente</span>
            </span>
          </div>
        </template>

        <template v-slot:cell(title)="data">
          <span @click="onRowClick(data.item)">{{ data.value }}</span>
        </template>

        <template v-slot:cell(total)="data">
          <span @click="onRowClick(data.item)">{{ data.value | dollarize }}</span>
        </template>

        <template v-slot:cell(status)="data">
          <b-badge
            v-if="data.item.is_open"
            pill
            :variant="data.value"
            class="clickable"
            style="font-size: 90%; width: 100px"
            @click="openStatusModal(data)"
          >
            <span>{{ data.value | titleize }}</span>
            <font-awesome-icon :icon="['fas', 'caret-down']" />
          </b-badge>
          <b-badge
            v-else
            pill
            variant="secondary"
            v-b-tooltip.hover.v-dark.topleft="data.value"
            style="font-size: 90%; width: 100px"
          >
            <span>Cerrada</span>
          </b-badge>
        </template>

        <template v-slot:cell(created_at)="data">
          <span @click="onRowClick(data.item)">{{ data.value | formatDate }}</span>
        </template>

        <template v-slot:cell(show_details)="row">
          <Button size="sm" style="width: 25px" @click="showRowDetails(row)">
            <font-awesome-icon v-if="row.detailsShowing" :icon="['fas', 'caret-down']" size="lg" fixed-width />
            <font-awesome-icon v-else :icon="['fas', 'caret-right']" size="lg" fixed-width />
          </Button>
        </template>

        <template v-slot:row-details="row">
          <span class="d-none">{{row}}</span>
          <b-row v-if="selectedQuoteItems" class="px-5">
            <b-table
              :items="quoteItems"
              :fields="subfields"
              primary-key="sap_line_num"
              head-variant="light"
              show-empty
              small
              class="fs-12 px-5"
              >
              <!-- Empty -->
              <template #empty>
                <div v-if="loadingItems" class="text-center text-info">
                  <b-spinner small type="grow"></b-spinner>
                  <span class="pl-2">Cargando items...</span>
                </div>
                <div v-else class="text-center text-danger">
                  <span>Oferta sin artículos</span>
                </div>
              </template>
              <!-- Sap price -->
              <template #cell(sap_price)="data">
                <div v-if="data.item.line_type == 'item'">
                  <span v-if="detailCRC">{{ data.item.item_price | colones2 }}</span>
                  <span v-else>{{ data.item.item_price | dollarize }}</span>
                </div>
              </template>
              <!-- Item Code -->
              <template #cell(item_code)="data">
                <span v-if="data.item.line_type == 'item'">{{data.item.product.item_code}}</span>
              </template>
              <!-- Item Description -->
              <template #cell(item_description)="data">
                <span v-if="data.item.line_type == 'item'">{{data.item.product.description}}</span>
                <span v-else class="font-weight-bold text-secondary"> {{ data.item.line_text }} </span>
              </template>
              <!-- quantity -->
              <template #cell(quantity)="data">
                <span v-if="data.item.line_type == 'item'">{{data.item.quantity}}</span>
              </template>
            </b-table>
          </b-row>
        </template>
      </b-table>
    </b-row>

    <!-- Page tab-->
    <div class="text-center my-3">
      <PaginationCountless :page="page" :next="next" :prev="prev">
        <template #prev-label>
          <font-awesome-icon :icon="['fas', 'backward']" size="lg" fixed-width />
        </template>
        <template #next-label>
          <font-awesome-icon :icon="['fas', 'forward']" size="lg" fixed-width />
        </template>
      </PaginationCountless>
    </div>

    <!-- Quote SAP modal -->
    <QuotesSapModal
      :search-term= searchTerm
      @modal-hidden="onModalHide"
      @update-search="updateSearchTerm"
    />

    <!-- Confirm modal  -->
    <b-modal
      v-model="searchSapQuotes"
      centered
      no-close-on-backdrop
      no-close-on-esc
      hide-header
      hide-footer
    >
      <b-row align-h="center" >
        <font-awesome-icon :icon="['far', 'question-circle']"  class="text-warning modal-icon" />
      </b-row>
      <b-row align-h="center">
        <b class="fs-20">¿Desea buscar la cotización en SAP?</b>
      </b-row>
      <b-row align-h="center" class="pt-2 pl-2 pr-2 text-secondary">
        <small class="text-center">NOTA: En caso de que este seguro que la cotización buscada no se encuentra en XYZ, presione 'Continuar'</small>
      </b-row>
      <b-row align-h="around" class="my-4">
        <Button variant="red" @click="searchSapQuotes = false">Cancelar</Button>
        <Button variant="green" @click="searchInSap">Continuar</Button>
      </b-row>
    </b-modal>

    <!-- Status Modal -->
    <Modal
      v-if="statusModalShow"
      contentWidth="600px"
      :required=true
      @close="closeStatusModal"
    >
      <template #header>
        <p class="fs-20  ml-1 mb-0" style="line-height: 0.675rem; color: #163D7D;">
          Cambiar status de cotización <span v-if="selectedQuote" class="font-weight-bold">"{{ selectedQuote.quote_id }}"</span>
        </p>
      </template>
      <template #body>
        <b-row align-h="center">
          <b-col lg="6" cols="7" class="border-right">
            <b-radio-group v-model="form.status" stacked>
              <b-radio value="baja">Baja probabilidad</b-radio>
              <b-radio value="media">Media probabilidad</b-radio>
              <b-radio value="alta">Alta probabilidad</b-radio>
              <b-radio value="licitación">Licitación</b-radio>
              <div v-if="selectedQuote && selectedQuote.is_open">
                <b-radio value="desestimada">Desestimada</b-radio>
                <b-radio value="alternativa">Alternativa</b-radio>
                <b-radio value="vendido">Vendido</b-radio>
                <b-radio value="perdida">Perdida</b-radio>
              </div>
            </b-radio-group>
          </b-col>
          <b-col lg="6" cols="5" class="pr-0 text-info">
            <small v-if="form.status === 'baja'">Baja probabilidad (menos de 50%)</small>
            <small v-if="form.status === 'media'">Media probabilidad (50 a 79%)</small>
            <small v-if="form.status === 'alta'">Alta probabilidad (más de 80%)</small>
            <small v-if="form.status === 'licitación'">Licitación Construcción</small>
            <small v-if="form.status === 'desestimada'">No es urgente, equipo funciona, lo va a dejar así</small>
            <small v-if="form.status === 'alternativa'">Ya compró una de las opciones</small>
            <small v-if="form.status === 'vendido'">Quedo abierta pero no se jalaron todas las líneas</small>
            <small v-if="form.status === 'perdida'">El trabajo lo hizo otra empresa o persona</small>
          </b-col>
        </b-row>
        <!-- closing notes -->
        <b-row v-if="closingStatusSelected" class="mt-1">
          <b-col>
            <hr class="mb-2">

            <b-alert show variant="info" class="text-center mb-2  px-2 py-1 d-flex align-items-center justify-content-center">
              <font-awesome-icon :icon="['far', 'info-circle']" fixed-width class="font-weight-bold" />
              <span class="font-weight-bold">El status seleccionado corresponde a un status de cierre</span>
            </b-alert>

            <b-form-group >
              <template #label>
                <span>Especifique los comentarios de cierre</span><span class="asterisk">*</span>
              </template>
              <b-textarea v-model="form.closing_notes" rows="4" class="ml-2" autofocus placeholder="Notas de cierre de cotización"></b-textarea>
            </b-form-group>

            <div class="text-center mt-2">
              <p class="text-danger text-center fs-14 mb-0">
                <font-awesome-icon :icon="['far', 'exclamation-triangle']" fixed-width />
                <span>El cierre de esta cotización es irreversible</span>
                <font-awesome-icon :icon="['far', 'exclamation-triangle']" fixed-width />
              </p>
            </div>
          </b-col>
        </b-row>
      </template>
      <template #footer>
        <Button
          variant="green"
          class="float-right"
          :disabled="!statusFormValid"
          @click="updateQuoteStatus"
        >
          Actualizar
        </Button>
      </template>
    </Modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import PaginationCountless from '../../shared/PaginationCountless';
import QuoteNavTabs from './QuoteNavTabs';
import QuotesSapModal from './QuotesSapModal';
import Button from '../../shared/Button.vue';
import Modal from '../../shared/Modal'
import UserSearchInput from '../../shared/UserSearchInput'
import { roundMoney } from '../../../utils/money'

export default {
  name: 'QuoteIndex',
  components: { PaginationCountless, QuoteNavTabs, QuotesSapModal, Button, Modal, UserSearchInput },
  data() {
    return {
      quotesItem: [],
      page: 1,
      next: null,
      prev: null,
      count: null,
      searchTerm: '',
      queryMode: false,
      scope: 'for_accesos',
      searchSapQuotes: false,
      lastRequestId: 0,
      fields: [
        { key: 'show_details', label: '', tdClass: 'text-center', thStyle: { width: '10px' } },
        { key: 'quote_id', label: 'ID', tdClass: 'clickable', thStyle: { width: '95px' } },
        { key: 'client_name', label: 'Cliente', tdClass: 'clickable', thStyle: { width: '270px' } },
        { key: 'title', label: 'Titulo', tdClass: 'clickable', thStyle: { width: '50px' }  },
        { key: 'total', label: 'Monto', sortable: true, tdClass: 'clickable text-right', thClass: 'text-right', thStyle: { width: '55px' } },
        { key: 'status', sortable: true, tdClass: 'clickable text-center', thClass: 'text-center', thStyle: { width: '60px' } },
        { key: 'created_at', label: 'Creado en', sortable: true, tdClass: 'clickable text-right', thClass: 'text-right', thStyle: { width: '60px' } }
      ],
      supervisorFields: [
        { key: 'show_details', label: '', tdClass: 'text-center', thStyle: { width: '10px' } },
        { key: 'user_id', label: 'Asesor',  sortable: true, tdClass: 'clickable', thStyle: { width: '70px' } },
        { key: 'quote_id', label: 'ID', tdClass: 'clickable', thStyle: { width: '50px' } },
        { key: 'client_name', label: 'Cliente', tdClass: 'clickable text-truncate', thStyle: { width: '270px' } },
        { key: 'title', label: 'Titulo', tdClass: 'clickable', thStyle: { width: '95px' }  },
        { key: 'total', label: 'Monto', sortable: true, tdClass: 'clickable text-right', thClass: 'text-right', thStyle: { width: '55px' } },
        { key: 'status', sortable: true, tdClass: 'clickable text-center', thClass: 'text-center', thStyle: { width: '60px' } },
        { key: 'created_at', label: 'Creado en', sortable: true, tdClass: 'clickable text-right', thClass: 'text-right', thStyle: { width: '60px' } }
      ],
      subfields: [
        { key: 'item_code', label: 'Código', tdClass: 'align-middle  text-center font-weight-bold', thClass: 'text-center', thStyle: { width: '70px' } },
        { key: 'item_description', label: 'Descripción', tdClass: 'align-middle  text-center', thClass: 'text-center', thStyle: { width: '300px' }},
        { key: 'quantity', label: 'Cantidad', tdClass: 'align-middle  text-center', thClass: 'text-center', thStyle: { width: '20px' }},
        { key: 'sap_price', label: 'Precio total', tdClass: 'align-middle  text-center', thClass: 'text-center', thStyle: { width: '70px' }}
      ],
      form: {
        status: null,
        closing_notes: null
      },
      loading: false,
      loadingItems: false,
      processing: false,
      noScroll: false,
      statusModalShow: false,
      selectedQuote: null,
      selectedQuoteItems: null,
      selectedQuoteDetail: null,
      selectedQuoteIndex: null
    }
  },
  computed: {
    ...mapGetters(['isSupervisor']),
    hideIgnored: {
      get () {
        return this.$store.state.quotes.hideIgnored
      },
      set (value) {
        this.$store.commit('quotes/setHideIgnored', value)
      }
    },
    hideClosed: {
      get () {
        return this.$store.state.quotes.hideClosed
      },
      set (value) {
        this.$store.commit('quotes/setHideClosed', value)
      }
    },
    sortBy: {
      get () {
        return this.$store.state.quotes.sortBy
      },
      set (value) {
        this.$store.commit('quotes/setSortBy', value)
      }
    },
    sortDesc: {
      get () {
        return this.$store.state.quotes.sortDesc
      },
      set (value) {
        this.$store.commit('quotes/setSortDesc', value)
      }
    },
    userFilter () {
      return this.$store.state.quotes.userFilter
    },
    selectedAdvisor () {
      return this.$store.state.quotes.selectedAdvisor
    },
    tableFields() {
      return this.isSupervisor ? this.supervisorFields : this.fields;
    },
    quoteParams () {
      return {
        q: this.searchTerm.trim(),
        scope: this.scope,
        hide_ignored: this.hideIgnored,
        hide_closed: this.hideClosed,
        sort_by: this.sortBy,
        sort_desc: this.sortDesc,
        user_id: this.userFilter
      }
    },
    activeFiltersCount () {
      return this.filters.length
    },
    statusFormValid () {
      if(!this.form.status || this.form.status == this.selectedQuote.status) return false

      if (this.closingStatusSelected && !this.form.closing_notes) return false

      return true
    },
    quotes() {
      let quotes = this.quotesItem
      if (quotes) {
        quotes = this.quotesItem.map(quote => {
          this.$set(quote, '_showDetails', false);
          return quote
        })
      }
      return quotes
    },
    quoteItems () {
      if (this.selectedQuoteItems) {
        let items = this.selectedQuoteItems
        items.map(item => {
          this.$set(item, 'item_price', roundMoney(this.listedPrice(item) * item.quantity));
          return item
        })
        return items.sort((a,b) => a.position - b.position)
      }
    },
    detailCRC () {
      return !!this.selectedQuoteDetail && this.selectedQuoteDetail.doc_currency === 'COL'
    },
    closingStatusSelected () {
      return ['desestimada', 'alternativa', 'vendido', 'perdida'].includes(this.form.status)
    },
    filters () {
      const list = []

      if (this.userFilter && this.selectedAdvisor) {
        const { concatName2, titleize } = this.$options.filters
        list.push({ key: 'user', text: `Asesor: ${titleize(concatName2(this.selectedAdvisor))}` })
      }

      return list
    }
  },
  watch: {
    '$route.query' (to, from) {
      this.fetchPaginatedQuotes();
    },
    scope(to, from) {
      this.reloadFromFirstPage();
    },
    hideIgnored(to, from) {
      this.reloadFromFirstPage();
    },
    hideClosed(to, from) {
      this.reloadFromFirstPage();
    }
  },
  mounted() {
    this.noScroll = true;
    this.fetchPaginatedQuotes();
  },
  methods: {
    fetchPaginatedQuotes() {

      this.loading = true;
      this.$http.get('/api/production/quotes', {
          params: {
            page: this.$route.query.pagina,
            ...this.quoteParams
          }
        })
        .then(response => {
          this.quotesItem = response.data.quotes;
          this.page = response.data.pagy.page;
          this.next = response.data.pagy.next;
          this.prev = response.data.pagy.prev;
          this.count = response.data.pagy.count;
          if (!this.noScroll) {
            window.scrollTo({
              top: 0,
              left: 0,
              behavior: 'smooth'
            });
          }
        })
        .catch(error => {
          console.log(error)
        })
        .finally(() => {
          this.loading = false;
          this.noScroll = false;
        });
    },
    reloadFromFirstPage () {
      this.noScroll = true
      if (this.$route.query.pagina) {
        const query = { ...this.$route.query }
        delete query.pagina
        this.$router.replace({ query }).catch(() => {})
      } else {
        this.fetchPaginatedQuotes()
      }
    },
    updateQuoteStatus () {
      let body = {
        sync: true,
        quote: { status: this.form.status }
      }

      if (this.closingStatusSelected) {
        body.quote.is_open = false
        body.quote.closed_on = new Date()
        body.quote.closing_notes = this.form.closing_notes
      }

      this.updateQuote(body)
    },
    updateQuote (body) {
      if (this.processing) return;
      this.processing = true
      this.$http.patch(`/api/production/quotes/${this.selectedQuote.id}`, body)
        .then(response => {
          this.quotesItem[this.selectedQuoteIndex].status = response.data.status
          this.quotesItem[this.selectedQuoteIndex].is_open = response.data.is_open
          this.quotesItem[this.selectedQuoteIndex].closing_notes = response.data.closing_notes
        })
        .catch(error => {
          console.log(error)
          // TODO:  alert
        })
        .finally(() => {
          this.processing = false
          this.statusModalShow = false
        });
    },
    onRowClick(item) {
      this.$router.push(`/app/prod/cotizador/${item.id}`);
    },
    search() {
      this.queryMode = true;
      this.$store.commit('quotes/resetSort');
      this.reloadFromFirstPage();
    },
    resetSearch() {
      this.queryMode = false;
      this.searchTerm = '';
      this.$store.commit('quotes/resetSort');
      this.reloadFromFirstPage();
    },
    onAdvisorSelected (user) {
      this.$store.commit('quotes/setAdvisor', user)
      this.reloadFromFirstPage()
    },
    onAdvisorReset () {
      this.$store.commit('quotes/setAdvisor', null)
      this.reloadFromFirstPage()
    },
    clearFilters () {
      this.onAdvisorReset()
    },
    removeFilter (key) {
      if (key === 'user') this.onAdvisorReset()
    },
    sortColumn(ctx) {
      if (ctx.sortBy) {
        this.noScroll = true;
        this.sortBy = ctx.sortBy
        this.sortDesc = ctx.sortDesc;
        this.fetchPaginatedQuotes();
      }
    },
    rowClass(item, type) {
      if (!item) return
      if (item.status === 'ignorar') return 'ignored-row'
    },
    searchInSap () {
      this.searchSapQuotes = false
      this.$bvModal.show('quotes-sap-modal')
    },
    updateSearchTerm (value) {
      this.searchTerm = value
    },
    onModalHide() {
      this.searchTerm = ''
      this.$bvModal.hide('quotes-sap-modal')
    },
    openStatusModal(data) {
      this.selectedQuote = data.item
      this.form.status = data.item.status
      this.form.closing_notes = data.item.closing_notes
      this.selectedQuoteIndex = data.index
      this.statusModalShow = true
    },
    closeStatusModal () {
      this.form.status = null
      this.form.closing_notes = null
      this.selectedQuoteIndex = null
      this.selectedQuote = null
      this.statusModalShow = false
    },
    showRowDetails(row){
      if (!row.detailsShowing) {
        this.selectedQuote = row.item
        if (this.processing) return;
        this.processing = true
        this.loadingItems = true
        this.$http.get(`/api/production/quotes/${row.item.id}`)
          .then(response => {
            this.selectedQuoteDetail = response.data.quote
            this.selectedQuoteItems = response.data.quote.quote_items
            this.quotes.forEach(quote => { row.item.id ===  quote.id ? quote._showDetails = true : quote._showDetails = false})
          })
          .catch(e => {
            console.log(e)
            this.quotes[row.index]._showDetails = false
          })
          .finally(() => {
            this.processing = false
            this.loadingItems = false
          });
      } else {
        this.quotes[row.index]._showDetails = false
        this.selectedQuote = null
        this.selectedQuoteItems = null
        this.selectedQuoteDetail = null
      }

    },
    productPrice(price, price_currency) {
      if (!price || !price_currency) { return 0 }
      const rate = this.selectedQuoteDetail.usd_crc_rate
      if (this.detailCRC) {
        return price_currency === 'COL' ? price : price * rate
      }
      return price_currency === 'COL' ? price / rate : price
    },
    listedPrice (quoteItem) {
      if (quoteItem.line_type === 'text' || !this.selectedQuoteDetail) { return 0 }
      if (quoteItem.is_custom) {
        let priceColones = quoteItem.custom_colones
        if (this.detailCRC && quoteItem.custom_colones == 0 && quoteItem.custom_dollars > 0) {
          priceColones = quoteItem.custom_dollars * this.selectedQuoteDetail.usd_crc_rate
        }
        return roundMoney(this.detailCRC ? priceColones : quoteItem.custom_dollars)
      }
      const pricingMode = this.selectedQuoteDetail.pricing_mode
      if (pricingMode === 'público') {
        return roundMoney(this.productPrice(quoteItem.retail_price, quoteItem.price_currency))
      } else if (pricingMode === 'instalador') {
        return roundMoney(this.productPrice(quoteItem.installer_price, quoteItem.installer_price_currency))
      } else if (pricingMode === 'mayorista') {
        return roundMoney(this.productPrice(quoteItem.wholesale_price, quoteItem.wholesale_price_currency))
      }
      return 0
    }
  }
}
</script>

<style scoped>
.form-control-sm {
  font-size: 12px !important;
  line-height: 2 !important;
}

.search-group {
  flex: 1 1 100%;
  min-width: 0;
}

@media (min-width: 576px) {
  .search-group {
    flex: 1 1 auto;
    min-width: 250px;
  }
}

@media (min-width: 768px) {
  .search-group {
    max-width: 450px;
  }
}

#filter-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.4rem;
  height: 1.4rem;
  padding: 0 0.35rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: #007bff;
  background-color: #fff;
  border-radius: 1rem;
}
.filter-item {
  padding: 0.2rem 0.4rem 0.2rem 0.7rem;
  color: #004085;
  background-color: #e7f1ff;
  border: 1px solid #b8daff;
  border-radius: 1rem;
}

.filter-item .btn {
  color: inherit;
  line-height: 1;
}

.filter-item .btn:hover {
  color: #dc3545;
}
</style>
